import axios from 'axios';
import oidcConfig from './cognitoConfig';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Request interceptor: attach Bearer id_token ─────────────────────────────
axiosClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('ar_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Refresh token helper ────────────────────────────────────────────────────
let isRefreshing = false;
let refreshQueue = []; // queued requests waiting for refresh

async function doTokenRefresh() {
  const refreshToken = sessionStorage.getItem('ar_refresh_token');
  if (!refreshToken) throw new Error('No refresh token');

  const body = new URLSearchParams({
    grant_type:    'refresh_token',
    client_id:     oidcConfig.clientId,
    refresh_token: refreshToken,
  });

  const res = await fetch(oidcConfig.tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) throw new Error('Refresh failed');

  const tokens = await res.json();
  const newIdToken = tokens.id_token;
  const expiresAt = Date.now() + ((tokens.expires_in || 1800) * 1000);

  // Persist new id_token for API calls
  sessionStorage.setItem('ar_token', newIdToken);
  sessionStorage.setItem('ar_token_expires_at', String(expiresAt));

  return newIdToken;
}

// ── Response interceptor: auto-refresh on 401 ──────────────────────────────
axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't already retried this request
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const newToken = await doTokenRefresh();

          // Retry all queued requests with the new token
          refreshQueue.forEach((cb) => cb(newToken));
          refreshQueue = [];

          // Retry the original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosClient(originalRequest);
        } catch (refreshErr) {
          // Refresh failed — clear session and redirect to login
          refreshQueue.forEach((cb) => cb(null));
          refreshQueue = [];
          console.warn('Token refresh failed — redirecting to login');
          sessionStorage.removeItem('ar_user');
          sessionStorage.removeItem('ar_token');
          sessionStorage.removeItem('ar_refresh_token');
          sessionStorage.removeItem('ar_token_expires_at');
          // Reload the current page — AuthProvider will detect no session and redirect to OIDC login
          window.location.reload();
          return Promise.reject(refreshErr);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Another request is already refreshing — queue this one
        return new Promise((resolve, reject) => {
          refreshQueue.push((newToken) => {
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(axiosClient(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }
    }

    // Extract error message
    const message = error.response?.data?.error || error.response?.data?.message || error.message || 'Request failed';
    return Promise.reject(new Error(message));
  }
);

export default axiosClient;
